import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FaSitemap } from '@react-icons/all-files/fa/FaSitemap';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageForm, { FormField, SelectOption } from '../../../../commons/PageForm';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../../manager/workflow-def.reducer';

const BACK = '/configuracion/workflow/definicion-workflow';

interface WorkflowDefForm {
  id: string;
  nombre: string;
  periodicidad: string;
  periodo: string;
  porDefecto: string;
  executorClass: string;
  workflowClase: string;
}

const empty: WorkflowDefForm = {
  id: '', nombre: '', periodicidad: '', periodo: '',
  porDefecto: 'false', executorClass: '', workflowClase: '',
};

export default function WorkflowDefFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity = useAppSelector(s => s.workflowDef.entity) as any;
  const updating = useAppSelector(s => s.workflowDef.updating);
  const updateSuccess = useAppSelector(s => s.workflowDef.updateSuccess);
  const errorMessage = useAppSelector(s => s.workflowDef.errorMessage) as string | null;

  const [form, setForm] = useState<WorkflowDefForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof WorkflowDefForm, string>>>({});
  const [claseOptions, setClaseOptions] = useState<SelectOption[]>([]);

  const { alert, showToast } = usePageAlert();

  // Fetch workflow classes directly — avoids polluting the paginated clase list state
  useEffect(() => {
    axios
      .get('api/profase-workflow-clases?page=0&size=999&sort=nombre,asc')
      .then(res => {
        const opts: SelectOption[] = (res.data as any[]).map(c => ({
          value: String(c.id),
          label: `${c.nombre}`,
        }));
        setClaseOptions(opts);
      })
      .catch(() => setClaseOptions([]));
  }, []);

  // Load the entity being edited
  useEffect(() => {
    if (!isNew) { dispatch(getEntity(id!)); }
    else { dispatch(reset()); setForm(empty); }
  }, [id]);

  useEffect(() => {
    if (!isNew && entity?.id) {
      setForm({
        id: String(entity.id),
        nombre: entity.nombre ?? '',
        periodicidad: entity.periodicidad ?? '',
        periodo: entity.periodo != null ? String(entity.periodo) : '',
        porDefecto: String(entity.porDefecto ?? false),
        executorClass: entity.executorClass ?? '',
        workflowClase: entity.workflowClase?.id != null ? String(entity.workflowClase.id) : '',
      });
    }
  }, [entity]);

  // Show error toast when the API call fails
  useEffect(() => {
    if (errorMessage) {
      showToast('error', errorMessage);
      dispatch(reset());
    }
  }, [errorMessage]);

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, {
        state: {
          successMsg: isNew
            ? 'Definición de workflow creada correctamente.'
            : 'Definición de workflow actualizada correctamente.'
        },
      });
    }
  }, [updateSuccess]);

  // Fields computed so workflowClase select gets live options
  const fields = useMemo((): FormField<WorkflowDefForm>[] => [
    {
      name: 'id',
      label: 'ID',
      readOnly: true,
      editOnly: true,
    },
    {
      name: 'nombre',
      label: 'Nombre',
      required: true,
      placeholder: 'Nombre de la definición',
    },
    {
      name: 'periodicidad',
      label: 'Periodicidad',
      placeholder: 'Ej: MENSUAL, ANUAL…',
    },
    {
      name: 'periodo',
      label: 'Período',
      type: 'number',
      placeholder: 'Ej: 1, 7, 30…',
    },
    {
      name: 'executorClass',
      label: 'Executor Class',
      placeholder: 'com.example.MyExecutor',
    },
    {
      name: 'workflowClase',
      label: 'Clase Workflow',
      type: 'select',
      options: claseOptions,
    },
    {
      name: 'porDefecto',
      label: 'Por Defecto',
      type: 'checkbox',
    },
  ], [claseOptions]);

  const handleFieldChange = (name: keyof WorkflowDefForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const e2: Partial<Record<keyof WorkflowDefForm, string>> = {};
    if (!form.nombre.trim()) e2.nombre = 'El nombre es requerido';
    if (Object.keys(e2).length) { setErrors(e2); return; }

    const payload: any = {
      ...(isNew ? {} : { id: Number(form.id) }),
      ...entity,
      nombre: form.nombre,
      periodicidad: form.periodicidad || null,
      periodo: form.periodo ? Number(form.periodo) : null,
      porDefecto: form.porDefecto === 'true',
      executorClass: form.executorClass || null,
      ...(form.workflowClase ? { workflowClase: { id: Number(form.workflowClase) } } : {})
    };

    if (isNew) dispatch(createEntity(payload));
    else dispatch(updateEntity(payload));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {alert && <PageAlert alert={alert} />}

      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Workflow', to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Definición Workflow', to: BACK, icon: <FaSitemap /> },
        { label: isNew ? 'Nueva Definición' : `Editar Definición #${id}` },
      ]} />

      <PageHeader
        icon={<FaSitemap />}
        iconColor="#3b82f6"
        title={isNew ? 'Nueva Definición Workflow' : 'Editar Definición Workflow'}
        subtitle={isNew
          ? 'Completa los campos para crear una nueva definición'
          : `Modificando definición #${id}`}
      />

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={isNew}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(BACK)}
        accentColor="#3b82f6"
      />
    </div>
  );
}
